// Historie van één taak, direct zichtbaar onder de opmerking — geen knop, geen popover.
// Bron: status_audit_log via de engine (loopt sinds 2 sep 2026), dezelfde data als de
// WijzigingslogKnop. Stil: kleine grijze regels, nieuwste eerst, na acht regels ingeklapt.
import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { authFetch } from '../../utils/authHelpers';
import { AI_ENGINE_URL } from '../../utils/apiConfig';

const FONT = '"Inter", system-ui, sans-serif';
const GEDEMPT = '#6b7280';
const ZACHT = '#9ca3af';
const TEKST = '#374151';
const EERST_ZICHTBAAR = 8;

/** "joris.van.dijk@fullframe.nl" → "Joris" (voornaam is genoeg in een stille regel). */
export const wieVanMail = (email) => {
  const lokaal = String(email || '').split('@')[0];
  if (!lokaal) return 'Systeem';
  const eerst = lokaal.split(/[._-]/)[0];
  return eerst.charAt(0).toUpperCase() + eerst.slice(1);
};

/** Datums in de log komen als "2026-10-03" of ISO-tijd; toon ze als "3 okt". */
const waardeTekst = (v) => {
  const s = String(v ?? '').trim();
  if (!s) return '—';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(`${s.slice(0, 10)}T12:00:00`);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
  }
  return s.length > 60 ? `${s.slice(0, 57)}…` : s;
};

const VELD_LABEL = {
  NaamTaak: 'naam', PrioNr: 'status', Afgerond: 'afgerond', Type: 'type', Opmerking: 'opmerking',
  StartDateUitvoerder: 'start', EndDateUitvoerder: 'eind', Deadline: 'deadline',
  TypeDeadline: 'soort deadline', UrenWerk: 'uren', Assignee: 'uitvoerder', Submitter: 'aanvrager',
};

/** Eén logregel als zin: "Joris zette deadline op 3 okt". */
export function historieZin(r) {
  const wie = wieVanMail(r.user_email);
  const veld = String(r.field || '');
  if (['aangemaakt', 'verwijderd'].includes(veld.toLowerCase())) return `${wie}: ${veld.toLowerCase()}`;
  const label = VELD_LABEL[veld] || veld.toLowerCase();
  if (veld === 'Opmerking') return `${wie} wijzigde de opmerking`;
  if (r.new_value === null || r.new_value === undefined || r.new_value === '') return `${wie} wiste ${label}`;
  return `${wie} zette ${label} op ${waardeTekst(r.new_value)}`;
}

const wanneer = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const nu = new Date();
  if (d.toDateString() === nu.toDateString()) return d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: d.getFullYear() !== nu.getFullYear() ? 'numeric' : undefined });
};

/**
 * @param taakId     id van de taak (rec… of loc_task_…)
 * @param versie     ophogen na een wijziging → de lijst haalt opnieuw op
 * @param onderaan   extra stille regels onderaan (bv. "Laatst gewijzigd door …", "Aangemaakt op …")
 */
export default function TaakHistorie({ taakId, versie = 0, onderaan = [] }) {
  const [log, setLog] = useState(null);
  const [alles, setAlles] = useState(false);

  useEffect(() => {
    let weg = false;
    if (!taakId || String(taakId).startsWith('outlook:')) { setLog([]); return undefined; }
    (async () => {
      try {
        const r = await authFetch(`${AI_ENGINE_URL}/api/v1/airtable/status-log/entity/${encodeURIComponent(taakId)}?limit=100`);
        const d = await r.json().catch(() => ({}));
        if (!weg) setLog(r.ok ? (d.log || []) : []);
      } catch {
        if (!weg) setLog([]);
      }
    })();
    return () => { weg = true; };
  }, [taakId, versie]);

  useEffect(() => { setAlles(false); }, [taakId]);

  const rijen = log || [];
  const zichtbaar = alles ? rijen : rijen.slice(0, EERST_ZICHTBAAR);
  const extra = onderaan.filter(Boolean);

  return (
    <Box component="section" aria-label="Historie">
      <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 600, color: GEDEMPT, mb: 0.75 }}>
        Historie
      </Typography>
      {log === null && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: ZACHT }}>Laden…</Typography>}
      {log && !rijen.length && !extra.length && (
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: ZACHT }}>
          Nog geen wijzigingen vastgelegd (de historie loopt sinds 2 september 2026).
        </Typography>
      )}
      {zichtbaar.map((r, i) => (
        <Box key={`${r.at}-${i}`} sx={{ display: 'flex', gap: 1.5, py: 0.4, alignItems: 'baseline' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: TEKST, flex: 1, minWidth: 0 }}>
            {historieZin(r)}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: ZACHT, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
            {wanneer(r.at)}
          </Typography>
        </Box>
      ))}
      {rijen.length > EERST_ZICHTBAAR && (
        <Box component="button" type="button" onClick={() => setAlles((a) => !a)}
          sx={{ border: 0, bgcolor: 'transparent', p: 0, mt: 0.5, cursor: 'pointer', fontFamily: FONT, fontSize: 12, color: GEDEMPT, '&:hover': { color: TEKST } }}>
          {alles ? 'Minder tonen' : `Alle ${rijen.length} wijzigingen`}
        </Box>
      )}
      {extra.map((regel) => (
        <Typography key={regel} sx={{ fontFamily: FONT, fontSize: 12, color: ZACHT, py: 0.4 }}>{regel}</Typography>
      ))}
    </Box>
  );
}
